/**
 * ACM theme — Longhorn-inspired schemata (UI manual).
 *
 * night      — noční ops (dark)
 * day        — Plex-light
 * slate      — neutrální šedá
 * primavera  — italské jaro (světlé, #00735C)
 * sera       — večerní pár Primavery (tmavé)
 * salon      — francouzský pop-art + měšťanský salon 60./70. let
 * m365       — Microsoft 365 / Fluent (světlé)
 * moon       — půlnoc: měsíc pod mrakem, matné stříbro (ne bílá záře)
 * apollo     — Apollo Multimedia: černý shell, brand orange z loga (#FF6B00)
 * stratosphere — navy #021F42, cyan #00A3E3, aqua #00FFE0 (CIE Group)
 *
 * Solární cyklus (`themeMode === 'cycle'`): jeden „život“ dne — mapuje
 * astronomické fáze (elevace slunce) na stávající schémata (primavera ↔ sera
 * rodina přes den/noc), bez ručního přepínání.
 *
 * Aplikace: data-theme na <html>, CSS variables → Tailwind gray scale.
 * Cache: localStorage; sync na účet: ui.prefs key `theme` (fire-and-forget).
 */
import i18n from './i18n';
import {
  getSolarSnapshot,
  readStoredSolarCoords,
  requestSolarGeolocation,
  solarPhaseLabel,
  type SolarPhase,
} from './solar';

export type AcmTheme =
  | 'night'
  | 'day'
  | 'slate'
  | 'primavera'
  | 'sera'
  | 'salon'
  | 'm365'
  | 'moon'
  | 'apollo'
  | 'stratosphere';

export type ThemeMode = 'manual' | 'cycle';

export const THEMES: readonly AcmTheme[] = [
  'night',
  'day',
  'slate',
  'primavera',
  'sera',
  'salon',
  'm365',
  'moon',
  'apollo',
  'stratosphere',
] as const;

/** 4 swatche pro miniaturu v ThemeSwitcher popup (shell, surface, brand, accent). */
export type ThemeSwatches = readonly [string, string, string, string];

export const THEME_META: Record<
  AcmTheme,
  { label: string; hint: string; longhorn: string; swatches: ThemeSwatches }
> = {
  night: {
    label: 'Night',
    hint: 'Noční ops — monitory, logy',
    longhorn: 'Slate-dark',
    swatches: ['#020617', '#0f172a', '#3b82f6', '#6366f1'],
  },
  day: {
    label: 'Day',
    hint: 'Denní Plex — světlé plochy',
    longhorn: 'Plex',
    swatches: ['#e8f0f8', '#ffffff', '#2563eb', '#93c5fd'],
  },
  slate: {
    label: 'Slate',
    hint: 'Neutrální šedá — audit',
    longhorn: 'Slate',
    swatches: ['#f4f4f5', '#ffffff', '#52525b', '#a1a1aa'],
  },
  primavera: {
    label: 'Primavera',
    hint: 'Italské jaro — mint, korál, #00735C',
    longhorn: 'Primavera',
    swatches: ['#EDEFF0', '#F8F6F6', '#00735C', '#F4B1A8'],
  },
  sera: {
    label: 'Sera',
    hint: 'Večerní pár Primavery — #111D1D + matný teal',
    longhorn: 'Sera',
    swatches: ['#111D1D', '#191B1C', '#009478', '#9F2B25'],
  },
  salon: {
    label: 'Salon',
    hint: 'FR pop-art + salon 60./70. — krém, hořčice, burgund',
    longhorn: 'Salon pop',
    swatches: ['#F7F1E8', '#FAF6EF', '#C9A227', '#9B2348'],
  },
  m365: {
    label: '365',
    hint: 'Microsoft 365 / Fluent — #0078D4, #F3F2F1',
    longhorn: 'Fluent',
    swatches: ['#F3F2F1', '#FFFFFF', '#0078D4', '#00B7C3'],
  },
  moon: {
    label: 'Moon',
    hint: 'Půlnoc — měsíc pod mrakem, matné stříbro',
    longhorn: 'Moon mono',
    swatches: ['#000000', '#101014', '#94949c', '#3a3a42'],
  },
  apollo: {
    label: 'Apollo',
    hint: 'Apollo Multimedia — černý portál, orange #FF6B00',
    longhorn: 'Apollo',
    swatches: ['#080808', '#100E0D', '#C45612', '#8F3A0C'],
  },
  stratosphere: {
    label: 'Stratosphere',
    hint: 'Stratosphere — navy #021F42, cyan #00A3E3, aqua #00FFE0',
    longhorn: 'Stratosphere',
    swatches: ['#021F42', '#0A1838', '#00A3E3', '#00FFE0'],
  },
};

export function themeHint(theme: AcmTheme): string {
  return i18n.t(`layout.theme.hint.${theme}`, THEME_META[theme].hint);
}

/**
 * Solární cyklus — jedna rodina vzhledu přes den:
 * světlé jaro (primavera) → den (day) → zlatá hodina (salon) →
 * západ (apollo) → večer (sera) → noc (night) → půlnoc (moon).
 */
export const CYCLE_PHASE_THEME: Record<SolarPhase, AcmTheme> = {
  midnight: 'moon',
  night: 'night',
  nautical_dawn: 'sera',
  civil_dawn: 'sera',
  sunrise: 'primavera',
  morning: 'day',
  noon: 'day',
  afternoon: 'slate',
  golden: 'salon',
  sunset: 'apollo',
  civil_dusk: 'sera',
  nautical_dusk: 'sera',
};

export const CYCLE_SWATCHES: ThemeSwatches = [
  '#EDEFF0',
  '#080808',
  '#C45612',
  '#00735C',
];

/** Light themes need inverted ink remaps (text-white → dark). */
export function isLightTheme(theme: AcmTheme): boolean {
  return (
    theme === 'day' ||
    theme === 'slate' ||
    theme === 'primavera' ||
    theme === 'salon' ||
    theme === 'm365'
  );
}

const STORAGE_KEY = 'acm_theme';
const MODE_KEY = 'acm_theme_mode';

export function isAcmTheme(v: unknown): v is AcmTheme {
  return (
    v === 'night' ||
    v === 'day' ||
    v === 'slate' ||
    v === 'primavera' ||
    v === 'sera' ||
    v === 'salon' ||
    v === 'm365' ||
    v === 'moon' ||
    v === 'apollo' ||
    v === 'stratosphere'
  );
}

export function readStoredTheme(): AcmTheme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'cie') return 'stratosphere';
    if (isAcmTheme(raw)) return raw;
  } catch {
    /* private mode */
  }
  return themeForNow().theme;
}

export function readThemeMode(): ThemeMode {
  try {
    const m = localStorage.getItem(MODE_KEY);
    if (m === 'cycle' || m === 'manual') return m;
    // Starší klienti mají jen acm_theme (ruční volba). Bez obojího → cyklus.
    if (isAcmTheme(localStorage.getItem(STORAGE_KEY))) return 'manual';
  } catch {
    /* ignore */
  }
  return 'cycle';
}

export function setThemeMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* ignore */
  }
  const root = document.documentElement;
  if (mode === 'cycle') {
    root.setAttribute('data-theme-mode', 'cycle');
  } else {
    root.removeAttribute('data-theme-mode');
  }
}

/** Aktuální solární snapshot → doporučené schéma. */
export function themeForNow(date: Date = new Date()): {
  theme: AcmTheme;
  phase: SolarPhase;
  phaseLabel: string;
  elevation: number;
} {
  const { lat, lon } = readStoredSolarCoords();
  const snap = getSolarSnapshot(date, lat, lon);
  const theme = CYCLE_PHASE_THEME[snap.phase];
  return {
    theme,
    phase: snap.phase,
    phaseLabel: solarPhaseLabel(snap.phase),
    elevation: snap.elevation,
  };
}

/** Nastaví data-theme na <html> + localStorage (manuální i cyklus). */
export function applyTheme(theme: AcmTheme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.style.colorScheme = isLightTheme(theme) ? 'light' : 'dark';
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
}

/** Manuální výběr schématu — vypne cyklus. */
export function applyManualTheme(theme: AcmTheme): void {
  stopThemeCycle();
  setThemeMode('manual');
  applyTheme(theme);
}

/** Zapne solární cyklus a hned aplikuje aktuální fázi. */
export function applyCycleTheme(): AcmTheme {
  setThemeMode('cycle');
  requestSolarGeolocation();
  const { theme, phase } = themeForNow();
  applyTheme(theme);
  document.documentElement.setAttribute('data-solar-phase', phase);
  startThemeCycle();
  try {
    window.dispatchEvent(
      new CustomEvent('acm-theme-cycle', { detail: { theme, phase } }),
    );
  } catch {
    /* ignore */
  }
  return theme;
}

let cycleTimer: ReturnType<typeof setInterval> | null = null;
let lastCycleTheme: AcmTheme | null = null;

function tickCycle(): void {
  if (readThemeMode() !== 'cycle') return;
  const { theme, phase } = themeForNow();
  document.documentElement.setAttribute('data-solar-phase', phase);
  if (theme !== lastCycleTheme) {
    lastCycleTheme = theme;
    applyTheme(theme);
    try {
      window.dispatchEvent(
        new CustomEvent('acm-theme-cycle', { detail: { theme, phase } }),
      );
    } catch {
      /* ignore */
    }
  }
}

export function startThemeCycle(): void {
  if (cycleTimer != null) return;
  lastCycleTheme = readStoredTheme();
  tickCycle();
  // 60 s — fáze se mění pomalu; geolokace / DST bez spamu
  cycleTimer = setInterval(tickCycle, 60_000);
}

export function stopThemeCycle(): void {
  if (cycleTimer != null) {
    clearInterval(cycleTimer);
    cycleTimer = null;
  }
  lastCycleTheme = null;
  document.documentElement.removeAttribute('data-solar-phase');
}

/** Init před prvním paintem (volat z main.tsx). */
export function initTheme(): AcmTheme {
  const mode = readThemeMode();
  if (mode === 'cycle') {
    setThemeMode('cycle');
    const { theme, phase } = themeForNow();
    applyTheme(theme);
    document.documentElement.setAttribute('data-solar-phase', phase);
    // timer až po load (setInterval v main safe); volá startThemeCycle z ThemeSwitcher/useAccount
    if (typeof window !== 'undefined') {
      // odložit ticker za first paint
      queueMicrotask(() => startThemeCycle());
    }
    return theme;
  }
  const t = readStoredTheme();
  applyTheme(t);
  return t;
}

export { solarPhaseLabel };
export type { SolarPhase };
